import type { FC } from "react";

import { RedirectionLinkCard } from "@ui/components/RedirectionLinkCard";
import { useCurrentBreakpoint } from "@ui/hooks/useCurrentBreakpoint";
import { getBuilderImageResizeUrl } from "@ui/utils/getBuilderImageResizeUrl";
import { getOptimizedBackgroundImage } from "@ui/utils/getOptimizedBackgroundImage";
import classNames from "classnames";

import { LinkBehaviour } from "@jti/core-components/components/LinkBehaviour";
import { dispatchTrackingEvent, TrackingEvents } from "@jti/tracker";

import styles from "./ShopTeaser.module.scss";

export type ShopTeaserProps = {
  title: string;
  isTitleHidden?: boolean;
  href: string;
  src: string;
  alt?: string;
  logoSrc?: string;
  isTopGradient?: boolean;
  isBottomGradient?: boolean;
  bottomText?: string;
  isLoyalty?: boolean;
  theme?: "inherit" | "dark" | "light";
};

export const ShopTeaser: FC<ShopTeaserProps> = ({
  title,
  isTitleHidden,
  src,
  alt = "",
  href,
  bottomText,
  isLoyalty,
  logoSrc,
  isBottomGradient,
  isTopGradient,
  theme,
}) => {
  const { mainBreakpoint } = useCurrentBreakpoint();
  const isMobile = ["xs", "sm"].includes(mainBreakpoint);

  const dispatchGTMEvent = () => {
    dispatchTrackingEvent(TrackingEvents.TrackingMenuNavigationListClick);
  };
  const isTitleShown = title && !isTitleHidden;

  // Optimize Background Image
  const backgroundResizedImageUrl = getBuilderImageResizeUrl({ imageUrl: src ?? "", width: 480, height: 247 });
  const optimizedBackgroundImage = getOptimizedBackgroundImage(backgroundResizedImageUrl ?? "", 480, 247);

  // Optimize Logo Image
  const logoResizedImageUrl = getBuilderImageResizeUrl({ imageUrl: logoSrc ?? "", width: 200, height: 100 });
  const optimizedLogoImage = logoSrc ? getOptimizedBackgroundImage(logoResizedImageUrl ?? "", 200, 100) : undefined;

  return (
    <div
      className={classNames({
        "dark-theme": theme === "dark",
        "light-theme": theme === "light",
      })}
    >
      {isMobile ? (
        <div className={styles.mobileVariant}>
          <RedirectionLinkCard link={href} title={title} variant={isLoyalty ? "ploomClub" : "withImage"} image={src} />
        </div>
      ) : (
        <div
          className={classNames(styles.wrapper, {
            [styles.withTopGradient]: isTopGradient && !isBottomGradient,
            [styles.withBottomGradient]: isBottomGradient && !isTopGradient,
            [styles.bothGradients]: isTopGradient && isBottomGradient,
            [styles.withBottomText]: bottomText,
            [styles.bottomTextOnlyPlacement]: !isTitleShown && bottomText,
          })}
          style={{
            "--bg-image-url": optimizedBackgroundImage,
          }}
          data-testid="shop-container"
        >
          <LinkBehaviour onClick={dispatchGTMEvent} href={href} aria-label={alt || undefined}>
            {isTitleShown && <span>{title}</span>}
            {logoSrc && (
              <div
                className={styles.logo}
                style={{
                  "--logo-image-url": optimizedLogoImage,
                }}
                data-testid="shop-teaser-logo"
              ></div>
            )}
            <span>{bottomText}</span>
          </LinkBehaviour>
        </div>
      )}
    </div>
  );
};
